/**
 * Source of truth for the Keyboard Shortcuts modal. Two flavors per
 * key — `mac` and `win` — because the symbols (and a few combos like
 * Bring-to-front / Send-to-back) intentionally differ between
 * platforms. The component reads `isMac()` once on mount and picks the
 * matching variant.
 *
 * Key visual sizing in the modal is driven by `kind`:
 *   - "wide"   : longer labels like `Ctrl`, `Enter`, `Click`
 *                (32px tall, 8/18 padding, fluid width)
 *   - "square" : single-glyph keys like `⌘`, `⌥`, `⇧`, letters, `+`
 *                (32×32, aspect-ratio 1/1)
 *
 * "name + or + name" combos are modeled as `OrCombo` — two key sequences
 * with the literal "or" rendered between them.
 */
export type Key = { kind: "wide" | "square"; label: string };

/** A single shortcut sequence like ⇧+⌘+G, rendered as boxes joined by `+`. */
export type KeyCombo = Key[];

/** Two valid combos for the same action, rendered as `combo1 or combo2`. */
export type OrCombo = { or: [KeyCombo, KeyCombo] };

export type Shortcut = {
  name: string;
  mac: KeyCombo | OrCombo;
  win: KeyCombo | OrCombo;
};

export type Section = {
  id: "tools" | "text" | "selection" | "edit" | "transform" | "arrange";
  title: string;
  items: Shortcut[];
};

/** Cheap one-shot platform check used at modal-mount time. */
export const isMacPlatform = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");
};

// ───────────────────────── Helpers ──────────────────────────────────
const square = (label: string): Key => ({ kind: "square", label });
const wide = (label: string): Key => ({ kind: "wide", label });

// Common keys reused across many shortcuts.
const CMD = square("⌘");
const SHIFT = square("⇧");
const OPT = square("⌥");
const CTRL = wide("Ctrl");
const ALT = square("Alt");
const ENTER = wide("Enter");
const ESC = wide("Esc");
const CLICK = wide("Click");

// ───────────────────────── Sections ─────────────────────────────────
export const SECTIONS: Section[] = [
  {
    id: "tools",
    title: "Tools",
    items: [
      { name: "Move", mac: [square("V")], win: [square("V")] },
      { name: "Scale", mac: [square("K")], win: [square("K")] },
      { name: "Rectangle", mac: [SHIFT, square("R")], win: [SHIFT, square("R")] },
      { name: "Line", mac: [SHIFT, square("L")], win: [SHIFT, square("L")] },
      { name: "Arrow", mac: [SHIFT, square("A")], win: [SHIFT, square("A")] },
      { name: "Ellipse", mac: [SHIFT, square("O")], win: [SHIFT, square("O")] },
      { name: "Polygon", mac: [SHIFT, square("P")], win: [SHIFT, square("P")] },
      { name: "Text", mac: [SHIFT, square("T")], win: [SHIFT, square("T")] },
      { name: "Hand", mac: [square("H")], win: [square("H")] },
      { name: "Add comment", mac: [square("C")], win: [square("C")] },
      { name: "Pick color", mac: [SHIFT, square("I")], win: [SHIFT, square("I")] },
    ],
  },
  {
    id: "text",
    title: "Text",
    items: [
      { name: "Bold", mac: [CMD, square("B")], win: [CTRL, square("B")] },
      { name: "Italic", mac: [CMD, square("I")], win: [CTRL, square("I")] },
      { name: "Underline", mac: [CMD, square("U")], win: [CTRL, square("U")] },
      { name: "Strikethrough", mac: [SHIFT, CMD, square("X")], win: [SHIFT, CTRL, square("X")] },
    ],
  },
  {
    id: "selection",
    title: "Selection",
    items: [
      { name: "Deep select", mac: [CMD, CLICK], win: [CTRL, CLICK] },
      { name: "Select all", mac: [CMD, square("A")], win: [CTRL, square("A")] },
      { name: "Select none", mac: [ESC], win: [ESC] },
      { name: "Select children", mac: [ENTER], win: [ENTER] },
      {
        name: "Select parent",
        mac: { or: [[square("\\")], [SHIFT, ENTER]] },
        win: { or: [[square("\\")], [SHIFT, ENTER]] },
      },
      { name: "Group selection", mac: [CMD, square("G")], win: [CTRL, square("G")] },
      { name: "Ungroup selection", mac: [SHIFT, CMD, square("G")], win: [SHIFT, CTRL, square("G")] },
      { name: "Show/Hide selection", mac: [SHIFT, CMD, square("H")], win: [SHIFT, CTRL, square("H")] },
      { name: "Lock/Unlock selection", mac: [SHIFT, CMD, square("L")], win: [SHIFT, CTRL, square("L")] },
    ],
  },
  {
    id: "edit",
    title: "Edit",
    items: [
      { name: "Copy", mac: [CMD, square("C")], win: [CTRL, square("C")] },
      { name: "Cut", mac: [CMD, square("X")], win: [CTRL, square("X")] },
      { name: "Paste", mac: [CMD, square("V")], win: [CTRL, square("V")] },
      { name: "Paste over selection", mac: [SHIFT, CMD, square("V")], win: [SHIFT, CTRL, square("V")] },
      { name: "Duplicate", mac: [CMD, square("D")], win: [CTRL, square("D")] },
      { name: "Copy properties", mac: [OPT, CMD, square("C")], win: [ALT, CTRL, square("C")] },
      { name: "Paste properties", mac: [OPT, CMD, square("V")], win: [ALT, CTRL, square("V")] },
    ],
  },
  {
    id: "transform",
    title: "Transform",
    items: [
      { name: "Flip horizontal", mac: [SHIFT, square("H")], win: [SHIFT, square("H")] },
      { name: "Flip vertical", mac: [SHIFT, square("V")], win: [SHIFT, square("V")] },
    ],
  },
  {
    id: "arrange",
    title: "Arrange",
    items: [
      { name: "Align left", mac: [OPT, square("A")], win: [ALT, square("A")] },
      { name: "Align horizontal centers", mac: [OPT, square("H")], win: [ALT, square("H")] },
      { name: "Align right", mac: [OPT, square("D")], win: [ALT, square("D")] },
      { name: "Align top", mac: [OPT, square("W")], win: [ALT, square("W")] },
      { name: "Align vertical centers", mac: [OPT, square("V")], win: [ALT, square("V")] },
      { name: "Align bottom", mac: [OPT, square("S")], win: [ALT, square("S")] },
      { name: "Tidy up", mac: [CTRL, OPT, square("T")], win: [CTRL, ALT, square("T")] },
      { name: "Distribute horizontal spacing", mac: [CTRL, OPT, square("H")], win: [CTRL, ALT, square("H")] },
      { name: "Distribute vertical spacing", mac: [CTRL, OPT, square("V")], win: [CTRL, ALT, square("V")] },
      { name: "Bring to front", mac: [OPT, CMD, square("]")], win: [CTRL, SHIFT, square("]")] },
      { name: "Bring forward", mac: [CMD, square("]")], win: [CTRL, square("]")] },
      { name: "Send backward", mac: [CMD, square("[")], win: [CTRL, square("[")] },
      { name: "Send to back", mac: [OPT, CMD, square("[")], win: [CTRL, SHIFT, square("[")] },
    ],
  },
];
